import get from 'lodash/get';
import { BLOCKS } from '@contentful/rich-text-types';
import { LINK_TYPE } from 'utils/constants/linkType';
import { getDocumentFields, getYoutubeVideoIdFromUrl } from 'utils/helper';
import { organisationMicrodata } from './data';
import { fetchVideoMetadata } from './fetchYoutubeMetadata';

export function getYoutubeVideoUrls(videoId) {
  return {
    contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
    thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
  };
}

export function getVideoIdFromEntry(entry) {
  const { type, url } = getDocumentFields(entry, ['type', 'url']);

  if (type !== LINK_TYPE.youTube || !url) {
    return null;
  }

  return getYoutubeVideoIdFromUrl(url);
}

function collectVideoIds(nodes, videoIds) {
  if (!nodes?.length) {
    return;
  }

  nodes.forEach((node) => {
    if (node.nodeType === BLOCKS.EMBEDDED_ENTRY) {
      const contentTypeId = get(node, 'data.target.sys.contentType.sys.id', '');

      if (contentTypeId === 'link') {
        const videoId = getVideoIdFromEntry(get(node, 'data.target', {}));

        if (videoId) {
          videoIds.add(videoId);
        }
      }
    }

    if (node.content) {
      collectVideoIds(node.content, videoIds);
    }
  });
}

export function getVideoIdsFromArticle(document) {
  const videoIds = new Set();

  collectVideoIds(document?.content, videoIds);

  return [...videoIds];
}

export async function fetchVideosMetadataMap(document) {
  const videoIds = getVideoIdsFromArticle(document);

  if (!videoIds.length) {
    return {};
  }

  const entries = await Promise.all(
    videoIds.map(async (videoId) => {
      const metadata = await fetchVideoMetadata(videoId);

      return metadata ? [videoId, metadata] : null;
    }),
  );

  return Object.fromEntries(entries.filter(Boolean));
}

export function getVideoObjectMicrodata({
  name,
  description,
  videoId,
  uploadDate,
  duration,
  thumbnailUrl: videoThumbnailUrl,
}) {
  if (!name || !videoId) {
    return null;
  }

  const { contentUrl, embedUrl, thumbnailUrl } = getYoutubeVideoUrls(videoId);

  const videoObject = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    thumbnailUrl: videoThumbnailUrl || thumbnailUrl,
    contentUrl,
    embedUrl,
    publisher: organisationMicrodata,
  };

  if (description) {
    videoObject.description = description;
  }

  if (uploadDate) {
    videoObject.uploadDate = uploadDate;
  }

  if (duration) {
    videoObject.duration = duration;
  }

  return videoObject;
}
